import { Button, Card } from '@mantine/core';
import { FC, MouseEvent, useMemo } from 'react';

import AppointmentTypeIndicator from '@/components/AppointmentTypeIndicator.tsx';
import InstanceIndicator from '@/components/InstanceIndicator.tsx';
import PriorityMarker from '@/components/PriorityMarker.tsx';
import AppointmentCardHeader from '@/components/room-view/AppointmentCard/AppointmentCardHeader.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import useCheckInData from '@/hooks/useCheckInData.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';
import usePrismic from '@/hooks/usePrismic.tsx';

import { PrismicDataType } from '@/models/PrismicHookTypes.ts';
import { DailyAppointment, Priority } from '@/models/appointment/Appointment.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { checkoutAppointment } from '@/services/appointmentService.ts';

import { useAppointmentCheckinStore } from '@/stores/appointmentCheckinStore.ts';

import {
    getCalendarId,
    getGenericPatientFromAppointment,
    getInstanceId,
    getPvsPatientId,
    isCheckedIn,
    isCheckedOut,
    isCheckinArrived,
} from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { MISSING_PATIENT_ID, getInsuranceByKey, getPatientDisplayName } from '@/utils/patientUtils.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

interface Props {
    appointment: DailyAppointment;
    checkInData?: UserAppointmentDto;
    onClick: (appointment: DailyAppointment) => void;
    onMutateAppointments: () => void;
    isDischarged?: boolean;
}

const ResolvedAppointmentCard: FC<Props> = ({
    appointment,
    onClick,
    checkInData,
    onMutateAppointments,
    isDischarged = false,
}) => {
    const insurance = useMemo(() => getInsuranceByKey(appointment.data.insurance_coverage_type), [appointment]);
    const { data: professional_profiles } = usePrismic(PrismicDataType.PROFESSIONAL_PROFILE);
    const { appointments: appointments_checkin } = useAppointmentCheckinStore();
    const { data } = useEnrichedPractitionerDataForSpecificInstance(getInstanceId(appointment) ?? '');
    const { mutate: mutateCheckinData } = useCheckInData();
    const { setAppointmentCheckingIn } = useAppointmentCheckinStore();

    const allow_checkout =
        (isCheckinArrived(checkInData?.appointment?.checkin_status) ||
            isCheckedIn(checkInData?.appointment?.checkin_status)) &&
        !isCheckedOut(checkInData?.appointment?.checkout_status);

    // Missing patient id are schedule created on DC without any patient attached. We don't support them.
    const missing_patient_id: boolean =
        !getPvsPatientId(appointment) || getPvsPatientId(appointment) === MISSING_PATIENT_ID;
    const displayName = getPatientDisplayName(appointment);
    const loading_patient =
        appointments_checkin[appointment.id]?.is_checking_in || (!missing_patient_id && !displayName);
    const disabled = loading_patient;

    const handleButtonClick = async (e: MouseEvent): Promise<void> => {
        e.stopPropagation();

        const practice_id =
            data?.practitioners.find(({ id }) => id === appointment.professional.id)?.practices[0]?.pvs?.id ?? '';
        if (allow_checkout) {
            // checkout
            setAppointmentCheckingIn(appointment.id, true);
            await checkoutAppointment(appointment, checkInData?.number || '');
            onMutateAppointments();
            await mutateCheckinData();
            setAppointmentCheckingIn(appointment.id, false);
        } else {
            if (!appointment.patient) return;

            // Follow-up appointment
            openAppointmentScheduler({
                mode: 'book',
                patient: getGenericPatientFromAppointment(appointment),
                onClose: () => {
                    onMutateAppointments();
                },
                prefilled_values: {
                    pvs_provider: appointment.pvs_provider,
                    practice_doc_cirrus_id: practice_id,
                    insurance: getInsuranceByKey(appointment.data.insurance_coverage_type),
                    calendar_pvs_id: getCalendarId(appointment),
                },
            });
        }
    };

    return (
        <Card
            className="relative select-none items-center justify-between gap-2 rounded-lg border border-neutral-200 shadow-md transition duration-100 hover:bg-slate-50 lg:flex-row xl:p-6 dark:bg-neutral-800 dark:hover:bg-neutral-700"
            role="button"
            onClick={() => onClick(appointment)}
        >
            <InstanceIndicator instanceId={getInstanceId(appointment) ?? ''} />
            <AppointmentTypeIndicator appointment={appointment} />
            <PriorityMarker priority={appointment.priority ?? Priority.NORMAL} />
            <div className="flex flex-1 flex-col gap-1.5">
                <AppointmentCardHeader
                    patientName={displayName}
                    insurance={insurance}
                    isInsuranceCardActive={appointment.data.is_insurance_card_active}
                    date={appointment.start}
                    loading={loading_patient}
                    checkinData={checkInData}
                    isOnline={appointment.is_online}
                    isDischarged={isDischarged}
                    isNoShow={appointment.is_no_show}
                    appointment={appointment}
                    onTicketUpdate={async () => {
                        await mutateCheckinData();
                        onMutateAppointments();
                    }}
                />
                <Paragraph className="text-xs text-slate-500 xl:text-sm">{`${dayjs(appointment.start).format('HH:mm')} | ${appointment.data.services[0]?.name} | ${getTextFromRichTextField(professional_profiles?.get(appointment.professional.key)?.display_name)}`}</Paragraph>
            </div>
            <Button
                disabled={disabled}
                variant={allow_checkout ? 'filled' : 'outline'}
                onClick={handleButtonClick}
                classNames={{ label: 'text-xs xl:text-sm', root: 'w-full lg:w-auto' }}
            >
                {allow_checkout ? 'Entlassen' : 'Folgetermin'}
            </Button>
        </Card>
    );
};

export default ResolvedAppointmentCard;
